/**
 * Slack channel picker options from slack.listChannelBindings.
 *
 * NOTE: slack.listChannelBindings is currently adminOnly. Workflow authoring is requireAuth
 * (any member), so a plain member will get a FORBIDDEN response. The hook degrades gracefully
 * to isEmpty=true when the request fails, so non-admin members see the "no channel" CTA
 * rather than a crash.
 *
 * // TODO(H3): channelBindingId + member-readable bindings
 * When the backend adds member-readable bindings (or enriches the existing endpoint with
 * channel names), update the shape here to `{ value: channelId, label: '#channel-name' }`.
 * For now, the `label` falls back to the channelId when no name is available.
 */
import { useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

export interface ChannelOption {
  value: string;
  label: string;
}

export interface UseChannelOptionsReturn {
  options: ChannelOption[];
  isEmpty: boolean;
  isLoading: boolean;
  /** True when the request was denied (admin-only endpoint, member caller). */
  isAccessDenied: boolean;
}

export function useChannelOptions(): UseChannelOptionsReturn {
  const orpc = useOrpc();
  const { data, isLoading, error } = useQuery({
    ...orpc.slack.listChannelBindings.queryOptions({ input: {} }),
    // Silently degrade on FORBIDDEN — non-admin members get the guidance CTA
    retry: false,
  });

  const isForbidden =
    error !== null &&
    error instanceof Error &&
    (error.message.toLowerCase().includes('forbidden') || error.message.toLowerCase().includes('unauthorized'));

  if (error && !isForbidden) {
    // Network error or unexpected error — surface as empty / degraded
  }

  const bindings = data ?? [];

  const options: ChannelOption[] = (Array.isArray(bindings) ? bindings : []).map(
    (b: { channelId: string; channelName?: string | null }) => ({
      value: b.channelId,
      // The backend resolves channel_id → "#name" via the Slack API (best-effort); fall back to the
      // raw id when the name couldn't be resolved (no install / API error).
      label: b.channelName ?? b.channelId,
    }),
  );

  return {
    options,
    isEmpty: !isLoading && options.length === 0,
    isLoading,
    isAccessDenied: isForbidden,
  };
}
